import { CopyOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { selectAgGridTheme } from "@org-avp/avp-avengers-ui-shared-store";
import {
  CellClickedEvent,
  CellEditingStoppedEvent,
  ColDef,
  DefaultMenuItem,
  GetContextMenuItems,
  GetRowIdFunc,
  GridOptions,
  MenuItemDef,
  RowDoubleClickedEvent,
  SelectionChangedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Flex, Space } from "antd";
import { FC, useCallback, useMemo, useRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useSelectionOptions } from "../../../utils/grid-selection-utils";
import { ActionToolbarButton } from "../../context-action/ActionToolbarButton";
import { actionToAgGridMenuItem, ContextAction } from "../../context-action/context-action";
import { createGridShortcutHandler, useGridSelectAllAction } from "../../context-action/shortcuts";
import { MarketingRuleTableContentColumn, MarketingRuleTableRowData } from "./marketing-data-table-utils";
import { useMarketingRuleTableColumnDefinitions } from "./use-marketing-rule-table-column-definitions";

export interface MarketingDataTableRowParams {
  // --- management data ---
  // translation
  tNS: string;
  tPrefix: string;

  // --- content data ---
  // content to display
  rowData: MarketingRuleTableRowData[] | undefined;
  columns: MarketingRuleTableContentColumn[];

  // content management
  updateMarketingRuleData: (data: MarketingRuleTableRowData[]) => void;

  setSelectedMarketingRuleDataId(index: number | undefined): void;

  setMultiSelectedMarketingRuleDataIds(indices: number[]): void;

  addMarketingRuleDataEntry(copySelected?: boolean): () => void;

  removeSelectedMarketingRuleDataEntries(): void;

  isCopyEnabled: boolean;

  customContextMenuActions?: ContextAction[];
}

export const MarketingDataTableRow: FC<MarketingDataTableRowParams> = (
  {
    tNS, tPrefix,
    rowData, columns,
    updateMarketingRuleData,
    setSelectedMarketingRuleDataId,
    setMultiSelectedMarketingRuleDataIds,
    addMarketingRuleDataEntry,
    removeSelectedMarketingRuleDataEntries,
    isCopyEnabled,
    customContextMenuActions,
  },
) => {
  const { t: tCustom } = useTranslation(tNS);
  const { t: tDialog } = useTranslation("marketingDataDialogs");

  // --- grid management and content handling definitions ---
  const gridApiRef = useRef<AgGridReact<MarketingRuleTableRowData>>(null);

  const gridTheme = useSelector(selectAgGridTheme);

  // recognize entries
  const getRowId: GetRowIdFunc<MarketingRuleTableRowData> = useCallback(({ data }) => `${data.index}`, []);

  const columnDefs = useMarketingRuleTableColumnDefinitions(columns, tNS, tPrefix);

  const onCellClicked = useCallback((event: CellClickedEvent<MarketingRuleTableRowData>) => {
    if (event.event != null && event.event instanceof MouseEvent && event.rowIndex !== null && event.node.data && rowData
      && event.column.getColId() === "defaultTransformation" && !rowData[event.rowIndex].defaultTransformation) {
      const newMarketingRuleTableRows: MarketingRuleTableRowData[] = [];
      for (let i = 0; i < rowData.length; i++) {
        if (rowData[i].defaultTransformation) {
          rowData[i].defaultTransformation = false;
          newMarketingRuleTableRows.push(rowData[i]);
        }
      }

      rowData[event.rowIndex].defaultTransformation = true;
      newMarketingRuleTableRows.push(rowData[event.rowIndex]);

      updateMarketingRuleData(newMarketingRuleTableRows);
    }

    if (event.event != null && event.event instanceof MouseEvent && event.event.altKey && event.rowIndex !== null) {
      // stop editing any other cell before start editing a new cell
      event.api.stopEditing(false);
      event.api.startEditingCell({
        colKey: event.column,
        rowIndex: event.rowIndex,
      });
    }
  }, [rowData, updateMarketingRuleData]);

  const onRowDoubleClicked = useCallback((param: RowDoubleClickedEvent<MarketingRuleTableRowData>) => param.api.stopEditing(true), []);

  const onSelectionChanged = useCallback((params: SelectionChangedEvent<MarketingRuleTableRowData>) => {
    const rows = params.api.getSelectedRows();
    setMultiSelectedMarketingRuleDataIds(rows.map(({ index }) => index));
    setSelectedMarketingRuleDataId(rows.length === 1 ? rows[0].index : undefined);
  }, [setMultiSelectedMarketingRuleDataIds, setSelectedMarketingRuleDataId]);

  const onCellEditingStopped = useCallback((params: CellEditingStoppedEvent<MarketingRuleTableRowData>) => {
    if (params.valueChanged && params.data)
      updateMarketingRuleData([params.data]);
  }, [updateMarketingRuleData]);

  const defaultColDef = useMemo<ColDef<MarketingRuleTableRowData>>(() => ({
    flex: 1,
  }), []);


  const onCellContextMenu = useCallback<NonNullable<GridOptions["onCellContextMenu"]>>(
    (event) => event.node.setSelected(true, !event.node.isSelected()),
    [],
  );

  const selectionOptions = useSelectionOptions(true);
  const selectAllAction = useGridSelectAllAction(gridApiRef);
  const gridShortcutHandler = useMemo(
    () => createGridShortcutHandler(selectAllAction),
    [selectAllAction],
  );

  const ruleEntryActions: ContextAction[] = useMemo(
    () => [
      new ContextAction({
        action: addMarketingRuleDataEntry(false),
        icon: <PlusOutlined/>,
        textKey: "contextMenu.label.add",
      }),
      new ContextAction({
        action: addMarketingRuleDataEntry(true),
        disabled: !isCopyEnabled,
        icon: <CopyOutlined/>,
        textKey: "contextMenu.label.copy",
      }),
      new ContextAction({
        action: removeSelectedMarketingRuleDataEntries,
        icon: <DeleteOutlined/>,
        textKey: "contextMenu.label.remove",
      }),
    ],
    [addMarketingRuleDataEntry, isCopyEnabled, removeSelectedMarketingRuleDataEntries],
  );

  const getContextMenuItems: GetContextMenuItems<MarketingRuleTableRowData> = useCallback(
    () => {
      const result: (DefaultMenuItem | MenuItemDef<MarketingRuleTableRowData>)[] = [];
      result.push(...ruleEntryActions.map(
        (it) => actionToAgGridMenuItem<MarketingRuleTableRowData>(it, tDialog),
      ));

      if (customContextMenuActions != null) {
        result.push("separator", ...customContextMenuActions.map(
          (it) => actionToAgGridMenuItem<MarketingRuleTableRowData>(it, tCustom),
        ));
      }

      result.push("separator", "copy", "copyWithHeaders", "export");
      return result;
    },
    [customContextMenuActions, ruleEntryActions, tCustom, tDialog],
  );

  return (
    <Flex vertical={false} gap="small" style={{ height: "100%" }}>
      <div style={{ flex: "0 0 232px" }}>
        <Trans ns={tNS} i18nKey={tPrefix + "fieldLabel.marketingRules"}/>
      </div>
      <div className={gridTheme} style={{ height: "100%", flex: "1 1" }}>
        <AgGridReact<MarketingRuleTableRowData>
          onCellKeyDown={gridShortcutHandler}
          ref={gridApiRef}
          getRowId={getRowId}
          columnDefs={columnDefs}
          rowData={rowData}
          defaultColDef={defaultColDef}
          suppressClickEdit={true}
          stopEditingWhenCellsLoseFocus={true}
          rowSelection={selectionOptions}
          onCellClicked={onCellClicked}
          onRowDoubleClicked={onRowDoubleClicked}
          onSelectionChanged={onSelectionChanged}
          onCellEditingStopped={onCellEditingStopped}
          getContextMenuItems={getContextMenuItems}
          onCellContextMenu={onCellContextMenu}
        />
      </div>
      <div style={{ flex: 0 }}>
        <Space direction="vertical">
          {ruleEntryActions.map((action) => (
            <ActionToolbarButton action={action} t={tDialog} tooltipPlacement="right" key={action.textKey}/>
          ))}
        </Space>
      </div>
    </Flex>
  );
};
